import { useContext, useMemo } from 'react';
import { Theme, useTheme } from '@mui/material';
import { ScoringContext } from '@/contexts/scoring/context';
import {
  FIXTURE_ACTION_TYPE,
  FIXTURE_TYPE,
  SPORT_ID,
} from '@/service/constants';
import {
  FixtureConfig,
  FixtureConfigOptions,
  FixtureRules,
  MATCH_TYPE,
} from '@/service/types';
import { DEFAULT_SOCCER_PERIOD } from '@/constants';

export type FixtureInfoDisplay = {
  label: FIXTURE_INFO_LABEL;
  firstLine?: string;
  secondLine?: string;
  events?: FixtureInfoDisplayChip[];
  competitionChips?: FixtureInfoDisplayChip[];
};

type FixtureInfoDisplayChip = {
  chipName: CHIP_NAME;
  color: string;
  visible: boolean;
};

export enum CHIP_NAME {
  VAR = 'VAR',
  EXTRA_TIME = 'Extra time',
  PENALTY_SHOOTOUT = 'Penalty shootout',
  PERIOD_FORMAT = 'Period format',
  NO_LINEUPS = 'No lineups',
}

export enum FIXTURE_INFO_LABEL {
  NAME = 'Name',
  TYPE = 'Type',
  START_DATE_AND_TIME = 'Start date & time',
  SPORT = 'Sport',
  COMPETITION = 'Competition',
  STAGE = 'Stage',
  VENUE = 'Venue',
  CITY = 'City',
  COUNTRY = 'Country',
  VAR = 'VAR',
  EVENTS = 'Events',
}

const SOCCER_VAR_ACTIONS = [
  FIXTURE_ACTION_TYPE.POSSIBLE_VAR,
  FIXTURE_ACTION_TYPE.NO_VIDEO_CHECK,
  FIXTURE_ACTION_TYPE.VIDEO_CHECK_UNDERWAY,
  FIXTURE_ACTION_TYPE.VIDEO_CHECK_RESULT,
];

export const getFixtureDetails = (
  fixture: FixtureConfig['fixture'],
  theme: Theme,
  fixtureRules: FixtureRules,
  fixtureOptions: FixtureConfigOptions,
  sportId: number,
): Array<FixtureInfoDisplay> => {
  const date = new Date(fixture.startDateUTC);
  const dateString = date.toLocaleDateString(undefined, {
    dateStyle: 'medium',
  });
  const timeString = date.toLocaleTimeString(undefined, {
    timeStyle: 'short',
  });
  const { location } = fixture.venue;
  return [
    { label: FIXTURE_INFO_LABEL.NAME, firstLine: fixture.name },
    { label: FIXTURE_INFO_LABEL.TYPE, firstLine: FIXTURE_TYPE[fixture.type] },
    {
      label: FIXTURE_INFO_LABEL.START_DATE_AND_TIME,
      firstLine: dateString,
      secondLine: timeString,
    },
    {
      label: FIXTURE_INFO_LABEL.SPORT,
      firstLine: fixture.sport.name || '',
    },
    {
      label: FIXTURE_INFO_LABEL.COMPETITION,
      firstLine: fixture.competition.name || '',
      competitionChips: [
        {
          chipName: getPeriodFormat(sportId, fixtureRules) as CHIP_NAME,
          color: theme.palette.primary.main,
          visible: isVisible(
            CHIP_NAME.PERIOD_FORMAT,
            sportId,
            fixtureOptions,
            fixtureRules,
          ),
        },
        {
          chipName: CHIP_NAME.NO_LINEUPS,
          color: theme.palette.primary.main,
          visible: isVisible(
            CHIP_NAME.NO_LINEUPS,
            sportId,
            fixtureOptions,
            fixtureRules,
          ),
        },
      ],
    },
    {
      label: FIXTURE_INFO_LABEL.STAGE,
      firstLine: fixture.stage.name || '',
    },
    {
      label: FIXTURE_INFO_LABEL.EVENTS,
      events: [
        {
          chipName: CHIP_NAME.VAR,
          color: getColor(CHIP_NAME.VAR, theme, fixtureRules, sportId),
          visible: isVisible(CHIP_NAME.VAR, sportId),
        },
        {
          chipName: CHIP_NAME.EXTRA_TIME,
          color: getColor(CHIP_NAME.EXTRA_TIME, theme, fixtureRules, sportId),
          visible: isVisible(CHIP_NAME.EXTRA_TIME, sportId),
        },
        {
          chipName: CHIP_NAME.PENALTY_SHOOTOUT,
          color: getColor(
            CHIP_NAME.PENALTY_SHOOTOUT,
            theme,
            fixtureRules,
            sportId,
          ),
          visible: isVisible(CHIP_NAME.PENALTY_SHOOTOUT, sportId),
        },
      ],
    },
    { label: FIXTURE_INFO_LABEL.VENUE, firstLine: fixture.venue.name },
    { label: FIXTURE_INFO_LABEL.CITY, firstLine: location.city },
    {
      label: FIXTURE_INFO_LABEL.COUNTRY,
      firstLine: (location.country && location.country) || '',
    },
  ];
};

const getColor = (
  event: CHIP_NAME,
  theme: Theme,
  fixtureRules: FixtureRules,
  sportId: number,
): string => {
  switch (event) {
    case CHIP_NAME.VAR:
      const isSoccer = sportId === SPORT_ID.SOCCER;
      const isBasketballOrAmericanFootball = [
        SPORT_ID.BASKETBALL,
        SPORT_ID.AMERICAN_FOOTBALL,
      ].includes(sportId);

      const isActiveVar = isSoccer
        ? fixtureRules.VAR
        : isBasketballOrAmericanFootball;

      return isActiveVar
        ? theme.palette.snackbarColors.possibleVar.backgroundColor
        : theme.palette.secondary.light;

    case CHIP_NAME.EXTRA_TIME:
      return fixtureRules.ExtraTime
        ? theme.palette.primary.main
        : theme.palette.secondary.light;

    case CHIP_NAME.PENALTY_SHOOTOUT:
      return fixtureRules.PenaltyShootout
        ? theme.palette.primary.main
        : theme.palette.secondary.light;

    default:
      return theme.palette.secondary.light;
  }
};

const getPeriodFormat = (sportId: number, fixtureRules: FixtureRules) => {
  if (
    ![SPORT_ID.SOCCER, SPORT_ID.BASKETBALL].includes(sportId) ||
    !fixtureRules
  )
    return '';

  if (sportId === SPORT_ID.SOCCER) {
    const isCustomPeriod = fixtureRules.PeriodLength !== DEFAULT_SOCCER_PERIOD;

    return isCustomPeriod ? `2 x ${Number(fixtureRules.PeriodLength)} min` : '';
  }

  return fixtureRules && !!fixtureRules.MatchType
    ? `${fixtureRules.MatchType === MATCH_TYPE.HALVES ? '2' : '4'} x ${Number(
        fixtureRules.PeriodLength,
      )} min`
    : '';
};

const isVisible = (
  event: CHIP_NAME,
  sportId: number,
  fixtureOptions?: FixtureConfigOptions,
  fixtureRules?: FixtureRules,
): boolean => {
  switch (event) {
    case CHIP_NAME.VAR:
      return [
        SPORT_ID.SOCCER,
        SPORT_ID.BASKETBALL,
        SPORT_ID.AMERICAN_FOOTBALL,
      ].some((sport) => sport === sportId);
    case CHIP_NAME.EXTRA_TIME:
      return [SPORT_ID.SOCCER, SPORT_ID.AMERICAN_FOOTBALL].some(
        (sport) => sport === sportId,
      );
    case CHIP_NAME.PENALTY_SHOOTOUT:
      return sportId === SPORT_ID.SOCCER;
    case CHIP_NAME.PERIOD_FORMAT:
      return (
        ((!!fixtureRules?.MatchType && SPORT_ID.BASKETBALL === sportId) ||
          (SPORT_ID.SOCCER === sportId &&
            fixtureRules?.PeriodLength !== DEFAULT_SOCCER_PERIOD)) ??
        false
      );
    case CHIP_NAME.NO_LINEUPS:
      return (
        [SPORT_ID.SOCCER, SPORT_ID.BASKETBALL].includes(sportId) &&
        fixtureOptions?.sportRules.activePlayers === 0
      );
    default:
      return false;
  }
};

export const getVarVisibilityAndColor = (
  sportId: number,
  theme: Theme,
  actionTypeId?: number[],
) => {
  const isSoccer = sportId === SPORT_ID.SOCCER;
  const isBasketballOrAmericanFootball = [
    SPORT_ID.BASKETBALL,
    SPORT_ID.AMERICAN_FOOTBALL,
  ].includes(sportId);

  const isActiveVar = isSoccer
    ? !actionTypeId?.some((id) => SOCCER_VAR_ACTIONS.includes(id))
    : isBasketballOrAmericanFootball;

  const backgroundColor = isActiveVar
    ? theme.palette.snackbarColors.possibleVar.backgroundColor
    : theme.palette.secondary.light;

  return {
    visibleVar: isSoccer || isBasketballOrAmericanFootball,
    isActiveVar,
    backgroundColor,
  };
};

export const useFixtureDetails = () => {
  const {
    state: { currentSport },
    fixtureConfigState: { fixtureConfig, error, isLoading, mutate },
  } = useContext(ScoringContext);
  const theme = useTheme();

  const fixtureDetails = useMemo(() => {
    return {
      details: fixtureConfig
        ? getFixtureDetails(
            fixtureConfig?.fixture,
            theme,
            fixtureConfig?.fixtureRules,
            fixtureConfig.fixtureOptions,
            currentSport?.id ?? 0,
          )
        : null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixtureConfig, currentSport]);

  return {
    data: fixtureDetails,
    error,
    isLoading,
    mutate,
  };
};
